import { useMemo } from 'react'
import { PlaneLanding, Building2, PlaneTakeoff } from 'lucide-react'
import { TransientCard } from './TransientCard'
import type { TransientStayTableRow } from '@/types/transient-stays'
import { categorizeTodayTransients, formatCurrency } from '@/types/transient-stays'

interface TodayViewProps {
  transients: TransientStayTableRow[]
  selectedDate: Date
  onCheckIn?: (id: string) => void
  onCheckOut?: (id: string) => void
  onView?: (id: string) => void
}

/**
 * Today view with three sections:
 * - Arriving: Aircraft expected to arrive today
 * - On Ground: Aircraft currently checked in
 * - Departing: Aircraft expected to depart today
 */
export function TodayView({
  transients,
  selectedDate,
  onCheckIn,
  onCheckOut,
  onView,
}: TodayViewProps) {
  // Categorize transients into sections
  const sections = useMemo(
    () => categorizeTodayTransients(transients, selectedDate),
    [transients, selectedDate]
  )

  // Calculate daily revenue estimate
  const dailyRevenue = useMemo(() => {
    const onGroundRevenue = sections.onGround.reduce(
      (sum, t) => sum + (t.rate_amount || 0),
      0
    )
    const arrivingRevenue = sections.arriving.reduce(
      (sum, t) => sum + (t.rate_amount || 0),
      0
    )
    return onGroundRevenue + arrivingRevenue
  }, [sections])

  return (
    <div className="space-y-6">
      {/* Arriving Section */}
      <TodaySection
        icon={<PlaneLanding className="h-5 w-5" />}
        title="Arriving Today"
        count={sections.arriving.length}
        variant="arriving"
        emptyMessage="No arrivals scheduled for today"
      >
        {sections.arriving.map((transient) => (
          <TransientCard
            key={transient.id}
            transient={transient}
            variant="arriving"
            onCheckIn={onCheckIn}
            onView={onView}
          />
        ))}
      </TodaySection>

      {/* On Ground Section */}
      <TodaySection
        icon={<Building2 className="h-5 w-5" />}
        title="On the Ground"
        count={sections.onGround.length}
        variant="on_ground"
        emptyMessage="No aircraft currently on the ground"
      >
        {sections.onGround.map((transient) => (
          <TransientCard
            key={transient.id}
            transient={transient}
            variant="on_ground"
            onCheckOut={onCheckOut}
            onView={onView}
          />
        ))}
      </TodaySection>

      {/* Departing Section */}
      <TodaySection
        icon={<PlaneTakeoff className="h-5 w-5" />}
        title="Departing Today"
        count={sections.departing.length}
        variant="departing"
        emptyMessage="No departures scheduled for today"
      >
        {sections.departing.map((transient) => (
          <TransientCard
            key={transient.id}
            transient={transient}
            variant="departing"
            onCheckOut={onCheckOut}
            onView={onView}
          />
        ))}
      </TodaySection>

      {/* Daily Summary */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Today's Summary: {sections.arriving.length} arriving · {sections.onGround.length} on ground · {sections.departing.length} departing
          </span>
          <span className="font-medium">
            Transient Revenue Today: {formatCurrency(dailyRevenue)} (est.)
          </span>
        </div>
      </div>
    </div>
  )
}

// ============================================
// Section Component
// ============================================

interface TodaySectionProps {
  icon: React.ReactNode
  title: string
  count: number
  variant: 'arriving' | 'on_ground' | 'departing'
  emptyMessage: string
  children: React.ReactNode
}

function TodaySection({
  icon,
  title,
  count,
  variant,
  emptyMessage,
  children,
}: TodaySectionProps) {
  const iconColorClass = {
    arriving: 'text-blue-500',
    on_ground: 'text-green-500',
    departing: 'text-orange-500',
  }[variant]

  return (
    <div className="space-y-3">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <div className={iconColorClass}>{icon}</div>
        <h3 className="font-semibold uppercase text-sm tracking-wide">
          {title}
        </h3>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
          {count}
        </span>
      </div>

      {/* Section Content */}
      {count === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  )
}
